// ============================================================
// TankCharacter — the player (rendered via distark_render)
// ============================================================
import * as PIXI from 'pixi.js';
import { CharacterRigRenderer } from 'distark-render';
import { PhysicsBody } from '../physics/PhysicsBody.js';
import {
  TANK_HITBOX_W, TANK_HITBOX_H,
  PLAYER_SPEED, JUMP_FORCE, FRICTION_GROUND,
} from '../constants.js';
import { sound } from '../systems/SoundEngine.js';

// We let distark_render's autoFit resize the canvas to fit Tank exactly
const TANK_RENDER_SCALE = 0.18;   // display scale for the rendered character

// Pose definitions (rotationValues overrides applied to base rig)
// head: 0 overrides the default 0.61 rad tilt so Tank looks straight ahead
const HEAD_UP = { head: 0 };

// Base leg values from tank.json (lower legs nearly straight, ≈ 0)
// selfRotationValues for legs are -π — this corrects the image orientation.
// DO NOT rotate leftLeg/rightLeg by large amounts or the feet flip upside-down.
// Only animate thighs; tiny knee-bend values (< 0.5 rad) are safe.

// Base thigh values:  leftThigh ≈ -π,  rightThigh ≈ +π  (both straight down)
// A proper stride needs ~45° swing each side.
// Only animate thighs + tiny knee-bend. Never rotate legs by ±π or feet flip.

const POSES = {
  idle: {
    ...HEAD_UP,
  },

  // Leg direction rule (confirmed from jumping-jack preset data):
  //   thigh toward 0 (less extreme) = foot goes BACKWARD
  //   thigh away from 0 (more extreme) = foot goes FORWARD
  // Both thighs must move in the SAME relative direction each frame.

  // walkA — RIGHT foot forward, LEFT foot back
  //   both thighs toward 0
  walkA: {
    ...HEAD_UP,
    leftThigh:    -2.3,   // toward 0 → LEFT foot BACK
    rightThigh:    2.3,   // toward 0 → RIGHT foot FORWARD
    leftLeg:       0.05,  // back leg straight
    rightLeg:      0.35,  // forward knee bends naturally
    leftUpperArm: -2.6,   // left arm forward (contralateral to right lead leg)
    rightUpperArm: 3.6,   // right arm back
  },

  // walkB — LEFT foot forward, RIGHT foot back
  //   both thighs away from 0
  walkB: {
    ...HEAD_UP,
    leftThigh:    -3.85,  // away from 0 → LEFT foot FORWARD
    rightThigh:    3.85,  // away from 0 → RIGHT foot BACK
    leftLeg:       0.35,  // forward knee bends naturally
    rightLeg:      0.05,  // back leg straight
    leftUpperArm: -3.6,   // left arm back (contralateral to left lead leg)
    rightUpperArm: 2.6,   // right arm forward
  },

  jump: {
    ...HEAD_UP,
    head:          -0.2,
    leftThigh:    -2.7,   // legs tuck up
    rightThigh:    2.7,
    leftLeg:       0.4,   // knees bent
    rightLeg:      0.4,
    leftUpperArm: -2.4,
    rightUpperArm: 2.4,
  },

  fall: {
    ...HEAD_UP,
    head:          0.12,
    leftThigh:    -3.0,
    rightThigh:    3.0,
    leftLeg:       0.15,
    rightLeg:      0.15,
    leftUpperArm:  2.4,
    rightUpperArm: -2.4,
  },

  celebrate: {
    ...HEAD_UP,
    head:          -0.3,
    leftUpperArm: -1.8,
    rightUpperArm: 1.8,
    leftForearm:  -1.2,
    rightForearm:  1.2,
  },
};

export class TankCharacter {
  /**
   * @param {PIXI.Container} worldContainer - the PIXI container for the game world
   */
  constructor(worldContainer) {
    this.worldContainer = worldContainer;

    // ── Offscreen canvas for distark_render ──
    // Start with a generous size; autoFit will resize it
    this._canvas = document.createElement('canvas');
    this._canvas.width  = 800;
    this._canvas.height = 800;
    // willReadFrequently: true lets us efficiently call getImageData for bg removal
    this._ctx2d = this._canvas.getContext('2d', { willReadFrequently: true });

    this._renderer = new CharacterRigRenderer();
    this._rigData  = null;
    this._loadedImages = {};
    this._loaded   = false;
    this._renderPending = false;

    // ── PIXI sprite (canvas-backed) ──
    this._texture = PIXI.Texture.from(this._canvas);
    this.sprite   = new PIXI.Sprite(this._texture);
    this.sprite.anchor.set(0.5, 1.0);  // anchor at feet
    this.sprite.scale.set(TANK_RENDER_SCALE);
    this.sprite.zIndex = 30;
    worldContainer.addChild(this.sprite);

    // ── Physics body ──
    this.body = new PhysicsBody({
      x: 400, y: 400,
      width:  TANK_HITBOX_W,
      height: TANK_HITBOX_H,
      mass: 3,
      bounciness: 0.02,
      friction: FRICTION_GROUND,
    });
    this.body.entity  = this;
    this.body._noSleep = true;  // player never sleeps

    // ── Control state ──
    this.keys = { left: false, right: false, jump: false };
    this._jumpPressed   = false;
    this._jumpReleased  = true;

    // ── Animation state ──
    this._animState  = 'idle';
    this._walkTimer  = 0;
    this._walkPhase  = 0;  // 0 or 1
    this._animTimer  = 0;

    // ── Extra state ──
    this.dead      = false;
    this.won       = false;
    this.score     = 0;
    this.lives     = 3;
    this._hurtTimer = 0;
    this._hurtCooldown = 1.5;
    this._celebrateTimer = 0;

    // Interaction flags (set by SpawnedEntity triggers each frame)
    this._isPlayer       = true;
    this._insideLadder   = false;
    this._ladderEntity   = null;
    this._wasJumping     = false;
  }

  /** Load Tank's rig data and prime the image cache */
  async load() {
    try {
      const res = await fetch('/tank.json');
      this._rigData = await res.json();

      // autoFit sizes the canvas to exactly fit the character each render.
      // We use it on every frame (correct positioning, hat/feet always visible).
      // The only problem it caused was display SIZE changing per pose (shrinking
      // on jump).  We fix that by locking the display height separately.
      await this._renderer.render(
        this._canvas, this._rigData,
        undefined, undefined, false,
        { autoFit: true, canvasWidth: 2000, canvasHeight: 2000 }
      );

      // Lock the display height in pixels from the first (idle) render.
      // Every subsequent frame forces this exact display height, preventing
      // the sprite from shrinking for compact poses.
      this._lockedDisplayH = this._canvas.height * TANK_RENDER_SCALE;

      // Measure feet position so the sprite anchor aligns correctly.
      this._removeBackground();
      this._feetAnchorY = this._measureFeetFraction();
      this.sprite.anchor.set(0.5, this._feetAnchorY);

      this._rebuildTexture();
      this._loaded = true;
    } catch (e) {
      console.warn('[TankCharacter] Failed to load tank.json:', e);
      this._loaded = false;
    }
  }

  /** Return the fraction (0–1) of the canvas height where the feet are. */
  _measureFeetFraction() {
    const ctx  = this._ctx2d;
    const w    = this._canvas.width;
    const h    = this._canvas.height;
    const data = ctx.getImageData(0, 0, w, h).data;
    for (let row = h - 1; row >= 0; row--) {
      for (let col = 0; col < w; col++) {
        if (data[(row * w + col) * 4 + 3] > 20) {
          return Math.min((row + 6) / h, 1.0);
        }
      }
    }
    return 1.0;
  }

  _rebuildTexture() {
    this._removeBackground();
    // Destroy old texture and create a fresh one so PIXI picks up the
    // correct canvas dimensions (Texture.from caches by element reference
    // and can return stale size data if we've ever resized the canvas).
    if (this._texture) {
      try { this._texture.destroy(true); } catch(_) {}
    }
    this._texture = PIXI.Texture.from(this._canvas);
    this.sprite.texture = this._texture;
    this.sprite.scale.set(TANK_RENDER_SCALE);
  }

  /** Chroma-key out the distark_render background (#f0f0f0 ≈ rgb 240,240,240) */
  _removeBackground() {
    const ctx = this._ctx2d;
    if (!ctx) return;
    const w = this._canvas.width, h = this._canvas.height;
    if (w === 0 || h === 0) return;
    const imageData = ctx.getImageData(0, 0, w, h);
    const d = imageData.data;
    // Target: #f0f0f0 = 240,240,240
    // Tolerance MUST be < 15 so pure white (255,255,255) eye whites are NOT removed.
    // |255-240| = 15, so tolerance ≤ 14 keeps white; tolerance = 10 is safe.
    const tr = 240, tg = 240, tb = 240, tolerance = 10;
    for (let i = 0; i < d.length; i += 4) {
      if (
        Math.abs(d[i]   - tr) < tolerance &&
        Math.abs(d[i+1] - tg) < tolerance &&
        Math.abs(d[i+2] - tb) < tolerance
      ) {
        d[i+3] = 0;
      }
    }
    ctx.putImageData(imageData, 0, 0);
  }

  /** Update per frame. dt in seconds */
  update(dt) {
    if (this.dead || this.won) return;

    this._hurtTimer = Math.max(0, this._hurtTimer - dt);

    // Water splash sound
    const nowInWater = this.body.inWater;
    if (nowInWater && !this._wasInWater) sound.splash();
    this._wasInWater = nowInWater;

    // ── Ladder climbing ──────────────────────────────────
    // _insideLadder is set to true by ladder trigger each frame.
    // We reset it here; the trigger will re-set it if still inside.
    const onLadder = this._insideLadder;
    this._insideLadder = false;  // reset — trigger re-applies if still inside

    // ── Input → velocity ──
    const onGround = this.body.onGround;

    if (this.keys.left)  this.body.vx = -PLAYER_SPEED;
    else if (this.keys.right) this.body.vx = PLAYER_SPEED;
    else {
      this.body.vx *= onGround ? FRICTION_GROUND : 0.98;
      if (Math.abs(this.body.vx) < 4) this.body.vx = 0;
    }

    if (onLadder) {
      // On a ladder: gravity is cancelled, up/down moves vertically
      this.body.noGravity = true;
      this.body.vy = 0;
      if (this.keys.jump)                             this.body.vy = -PLAYER_SPEED;
      else if (this.keys.down || this.keys.right || this.keys.left) this.body.vy = PLAYER_SPEED * 0.6;
      // Jump off the ladder
      if (this.keys.jump && this._jumpReleased) {
        this.body.noGravity = false;
        this.body.vy = JUMP_FORCE;
        this._jumpReleased = false;
      }
    } else {
      this.body.noGravity = false;
    }

    // Normal jump (off ground or leaving ladder)
    if (!onLadder) {
      if (this.keys.jump && this._jumpReleased && onGround) {
        this.body.vy = JUMP_FORCE;
        this._jumpReleased = false;
      }
    }
    if (!this.keys.jump) this._jumpReleased = true;

    // ── Flip sprite ──
    const absScale = Math.abs(this.sprite.scale.x || TANK_RENDER_SCALE);
    if (this.body.vx < -5)  this.sprite.scale.x = -absScale;
    if (this.body.vx >  5)  this.sprite.scale.x =  absScale;

    // ── Animation state ──
    if (this._celebrateTimer > 0) {
      this._celebrateTimer -= dt;
      this._animState = 'celebrate';
    } else if (!onGround) {
      this._animState = this.body.vy < 0 ? 'jump' : 'fall';
    } else if (Math.abs(this.body.vx) > 20) {
      this._animState = 'walk';
      this._walkTimer += dt;
      if (this._walkTimer > 0.13) {
        this._walkTimer = 0;
        this._walkPhase ^= 1;
      }
    } else {
      this._animState = 'idle';
      this._walkTimer = 0;
    }

    // ── Re-render Tank ──
    this._animTimer += dt;
    if (this._animTimer > 0.05) {  // ~20fps for the rig
      this._animTimer = 0;
      this._renderRig();
    }

    // ── Sync PIXI sprite position (anchor at feet = bottom of hitbox) ──
    this.sprite.x = this.body.x;
    this.sprite.y = this.body.bottom; // feet at bottom of hitbox

    // ── Hurt flash ──
    this.sprite.alpha = this._hurtTimer > 0 && Math.floor(this._hurtTimer * 10) % 2 === 0
      ? 0.3
      : 1;
  }

  _renderRig() {
    if (!this._loaded || !this._rigData || this._renderPending) return;
    this._renderPending = true;

    const poseOverride = this._getPose();
    const modifiedRig = {
      ...this._rigData,
      rotationValues: {
        ...this._rigData.rotationValues,
        ...poseOverride,
      },
    };

    // autoFit on every frame keeps hat/feet correctly positioned by distark_render.
    // After render we force the display height to _lockedDisplayH so the sprite
    // never shrinks between poses.
    this._renderer.render(
      this._canvas, modifiedRig,
      undefined, undefined, false,
      { autoFit: true, canvasWidth: 2000, canvasHeight: 2000 }
    ).then(() => {
        this._removeBackground();
        this._texture.source.update();
        // Force constant display height (prevents shrinking on jump etc.)
        const scale = this._lockedDisplayH / this._canvas.height;
        const sign  = this.sprite.scale.x < 0 ? -1 : 1;
        this.sprite.scale.set(sign * scale, scale);
        this._renderPending = false;
      })
      .catch(() => { this._renderPending = false; });
  }

  _getPose() {
    switch (this._animState) {
      case 'walk':
        return POSES[this._walkPhase === 0 ? 'walkA' : 'walkB'];
      case 'jump':
        return POSES.jump;
      case 'fall':
        return POSES.fall;
      case 'celebrate':
        return POSES.celebrate;
      default:
        return POSES.idle;
    }
  }

  /** Respawn at a position */
  respawn(x, y) {
    this.body.x = x;
    this.body.y = y;
    this.body.vx = 0;
    this.body.vy = 0;
    this.dead = false;
    this._hurtTimer = 0;
  }

  /** Called when player touches a hazard */
  hurt() {
    if (this._hurtTimer > 0) return;
    this.lives--;
    this._hurtTimer = this._hurtCooldown;
    this.body.applyImpulse(0, -400); // knocked up slightly
    if (this.lives <= 0) this.die();
  }

  die() {
    this.dead = true;
    this.body.vy = -500;
    this.sprite.angle = 90;
    sound.die();
  }

  celebrate() {
    this._celebrateTimer = 2.0;
    this._animState = 'celebrate';
  }

  setPosition(x, y) {
    this.body.x = x;
    this.body.y = y;
  }

  /** Remove from world */
  destroy() {
    this.worldContainer.removeChild(this.sprite);
    this.sprite.destroy();
  }
}
